import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleTRAINING } from "@/components/training/ModuleTitleTRAINING";

const STATUS_LABEL = {
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Completed",
};
const STATUS_PILL = {
  not_started: "bg-sage-300/40 text-forest-800 border-sage-300",
  in_progress: "bg-terracotta-500/15 text-terracotta-600 border-terracotta-500/30",
  completed: "bg-forest-600 text-cream-100 border-forest-800",
};

export default function TrainingTeamCompliance() {
  const [reports, setReports] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/training/team");
      setReports(data.reports || []);
      setAssignments(data.assignments || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const t = assignments.length;
    const c = assignments.filter((a) => a.status === "completed").length;
    const o = assignments.filter((a) => a.overdue).length;
    return { t, c, o, rate: t ? Math.round((c / t) * 100) : 0 };
  }, [assignments]);

  return (
    <div className="space-y-6" data-testid="training-team-page">
      <div>
        <ModuleTitleTRAINING />
        <h1 className="mt-3 font-serif text-forest-900 text-4xl">Team Compliance</h1>
        <p className="mt-2 text-charcoal-soft">
          Read-only view of your direct reports&apos; training assignments and completion.
        </p>
      </div>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="patria-card p-4">
          <p className="text-xs uppercase tracking-widest text-charcoal-muted">Assigned</p>
          <p className="font-serif text-forest-900 text-3xl mt-1">{stats.t}</p>
        </div>
        <div className="patria-card p-4">
          <p className="text-xs uppercase tracking-widest text-charcoal-muted">Completed</p>
          <p className="font-serif text-forest-900 text-3xl mt-1">{stats.c}</p>
        </div>
        <div className="patria-card p-4">
          <p className="text-xs uppercase tracking-widest text-charcoal-muted">Rate</p>
          <p className="font-serif text-forest-900 text-3xl mt-1">{stats.rate}%</p>
        </div>
        <div className={`patria-card p-4 ${stats.o > 0 ? "border-l-4 border-terracotta-500" : ""}`}>
          <p className="text-xs uppercase tracking-widest text-charcoal-muted">Overdue</p>
          <p className="font-serif text-3xl mt-1 text-forest-900">{stats.o}</p>
        </div>
      </section>

      <section className="patria-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-forest-600 text-cream-100">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Teammate</th>
              <th className="text-left px-4 py-3 font-medium">Training</th>
              <th className="text-left px-4 py-3 font-medium">Required</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Due</th>
              <th className="text-left px-4 py-3 font-medium">Completed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sage-300/60">
            {assignments.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-charcoal-muted italic">
                  No assignments to your direct reports yet.
                </td>
              </tr>
            )}
            {assignments.map((a) => (
              <tr key={a.id} data-testid={`training-team-row-${a.id}`}>
                <td className="px-4 py-3">
                  <p className="text-forest-900 font-medium">{a.user_name}</p>
                  <p className="text-xs text-charcoal-muted">{a.department || "—"}</p>
                </td>
                <td className="px-4 py-3 text-charcoal">{a.training_title}</td>
                <td className="px-4 py-3">{a.required ? "Yes" : "No"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${STATUS_PILL[a.status]}`}>
                    {STATUS_LABEL[a.status]}
                  </span>
                </td>
                <td className={`px-4 py-3 ${a.overdue ? "text-terracotta-600 font-medium" : "text-charcoal-soft"}`}>
                  {a.due_date ? (a.due_date || "").split("T")[0] : "—"}
                  {a.overdue && " • OVERDUE"}
                </td>
                <td className="px-4 py-3 text-charcoal-soft">
                  {a.completed_at ? (a.completed_at || "").split("T")[0] : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
